import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type AxiosError,
  type AxiosResponseTransformer,
  type AxiosRequestTransformer,
} from "axios";
import humps from "humps";
import { apiUrl } from "../constants/environment";
import { useAuthStore } from "@/store/useAuth";
import { clearAllStores } from "@/store/clearStores";
import { refreshToken } from "@/services/user";
import { showToast } from "../utils/toast";
import { router } from "@/main";
const getToken = (): string | null => {
  return useAuthStore.getState().accessToken;
};
const getRefreshToken = (): string | null => {
  return useAuthStore.getState().refreshToken;
};

export const axiosInstance = axios.create({
  baseURL: apiUrl + "/api/v1",
});

const _refreshToken = async (): Promise<string> => {
  try {
    const _refreshToken = getRefreshToken();
    if (!_refreshToken) throw new Error("No refresh token found.");

    const resPromise = refreshToken({ refreshToken: _refreshToken });

    showToast.promise(resPromise, {
      titleLoading: "Re-authenticating your session...",
      titleSuccess: "Session refreshed successfully!",
      titleError: "Failed to refresh session. Please log in again.",
    });

    const res = await resPromise;
    useAuthStore.getState().setAccessToken(res.accessToken);
    return res.accessToken;
  } catch {
    throw new Error("Session expired. Please sign in again.");
  }
};

const request: AxiosInstance = axios.create({
  baseURL: apiUrl,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
  // Convert camelCase to snake_case
  transformRequest: [
    (data, headers) => {
      if (
        headers?.["Content-Type"] === "application/json" &&
        data &&
        typeof data === "object"
      ) {
        return JSON.stringify(humps.decamelizeKeys(data));
      }
      return data;
    },
    ...(axios.defaults.transformRequest as AxiosRequestTransformer[]),
  ],
  // Convert snake_case to camelCase
  transformResponse: [
    ...(axios.defaults.transformResponse as AxiosResponseTransformer[]),
    (data) => {
      if (data && typeof data === "object") {
        return humps.camelizeKeys(data);
      }
      return data;
    },
  ],
});

request.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

request.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest: any = error.config;

    // Handle 401 Unauthorized errors
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      try {
        const accessToken = await _refreshToken();
        if (accessToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return request(originalRequest);
        }
      } catch (refreshError) {
        clearAllStores();
        if (window.location.pathname !== "/sign-in") {
          router.navigate({ to: "/sign-in" });
        }
        showToast.error({
          title: "Session expired.",
          description: "Please sign in again.",
        });
        return Promise.reject(refreshError);
      }
    }

    // Handle different types of errors
    if (error.response) {
      // Server responded with an error status
      console.error("API Error:", {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers,
      });
    } else if (error.request) {
      // Request was made but no response received
      console.error("Network Error:", error.request);
    } else {
      // Something happened in setting up the request
      console.error("Request Error:", error.message);
    }

    return Promise.reject(error);
  }
);

export default request;
